import { ChangeEvent, FC, MouseEvent, useEffect, useState } from 'react';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { Buttons, Form, FormElements } from '../../../common/AddEditFormLayout/AddEditFormLayout.styles';
import {
  DropdownWrapperLarge,
  InputWrapperLarge,
  InputWrapperMedium
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import MaskedInput from '../../../components/MaskedInput/MaskedInput';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../components/RadioGroup/RadioGroup.types';
import Textarea from '../../../components/Textarea/Textarea';
import { AllowTopTypeKeys, ALLOW_TOPPING_TYPE, STATUS, StatusKeys } from '../../../constants/ApiEnums';
import { useEditProduct, useProduct } from '../../../services/product/product.service';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import { RadioWrapper, RowWrapper, TextAreaWrapper } from './EditProduct.styles';
import { HorizontalLine, SubTitle } from "../../Classes/ClassForm/ClassForm.styles";
import ImageForm from "../../../components/ImageForm/ImageForm";
import { readOnlyLabelTableText } from '../../../constants/GlobalTexts';

const EditProduct: FC = () => {
  const navigateWithToast = useNavigateWithToast();
  const queryClient = useQueryClient();
  const { classNo, productNo } = useParams();
  const _classNo = classNo as string;
  const _productNo = productNo as string;

  const { data, isError } = useProduct({ productNo: _productNo, classNo: _classNo }, true);
  const { mutate, isError: editProductIsError, isLoading, error: editProductError } = useEditProduct(_productNo);

  useFetchErrorToast([{ isError, label: 'Error fetching product data', id: 'productDataFetchError' }]);
  useGenericErrorMessage([{ isError: editProductIsError, error: editProductError }]);

  const productData = data?.data;

  const [formValues, setFormValues] = useState({
    allowToppingTypeNo: '',
    serviceGuaranteed: false,
    validOnWeb: false,
    overrideKitchenPrint: false,
    featureRefs: '',
    displayDesc: '',
    seqNo: '',
    statusNo: '',
    synopsis: '',
    webDisplay: '',
  });

  const [initialFormValues, setInitialFormValues] = useState({
    allowToppingTypeNo: '',
    serviceGuaranteed: false,
    validOnWeb: false,
    overrideKitchenPrint: false,
    featureRefs: '',
    displayDesc: '',
    seqNo: '',
    statusNo: '',
    synopsis: '',
    webDisplay: '',
  });

  const [formErrors, setFormErrors] = useState({
    displayDesc: '',
    featureRefs: '',
    seqNo: '',
    synopsis: '',
    webDisplay: '',
  });

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const checkForUnsavedChanges = () => {
    if (
      initialFormValues.allowToppingTypeNo === formValues.allowToppingTypeNo &&
      initialFormValues.serviceGuaranteed === formValues.serviceGuaranteed &&
      initialFormValues.validOnWeb === formValues.validOnWeb &&
      initialFormValues.overrideKitchenPrint === formValues.overrideKitchenPrint &&
      initialFormValues.featureRefs === formValues.featureRefs &&
      initialFormValues.displayDesc === formValues.displayDesc && 
      initialFormValues.seqNo === formValues.seqNo &&
      initialFormValues.statusNo === formValues.statusNo &&
      initialFormValues.synopsis === formValues.synopsis &&
      initialFormValues.webDisplay === formValues.webDisplay
    ) {
      setUnsavedChanges(false);
    } else {
      setUnsavedChanges(true);
    }
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [formValues]);

  useEffect(() => {
    if (productData) {
      setFormValues({
        allowToppingTypeNo: productData.allowToppingTypeNo?.toString() || '0',
        serviceGuaranteed: productData.serviceGuaranteed ?? false,
        validOnWeb: productData.validOnWeb ?? false,
        overrideKitchenPrint: productData.overrideKitchenPrint ?? false,
        featureRefs: productData.featureRefs?.toString() || '',
        displayDesc: productData.displayDesc,
        seqNo: productData.seqNo.toString(), 
        statusNo: productData.statusNo?.toString() || '',
        synopsis: productData.synopsis?.toString() || '',
        webDisplay: productData.webDisplay?.toString() || '',
      });
      setInitialFormValues({
        allowToppingTypeNo: productData.allowToppingTypeNo?.toString() || '0',
        serviceGuaranteed: productData.serviceGuaranteed ?? false,
        validOnWeb: productData.validOnWeb ?? false,
        overrideKitchenPrint: productData.overrideKitchenPrint ?? false,
        featureRefs: productData.featureRefs?.toString() || '',
        displayDesc: productData.displayDesc,
        seqNo: productData.seqNo.toString(),
        statusNo: productData.statusNo?.toString() || '',
        synopsis: productData.synopsis?.toString() || '',
        webDisplay: productData.webDisplay?.toString() || '',
      });
    }
  }, [productData]);

  const handleFeatureRefsChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    setFormErrors({
      ...formErrors,
      featureRefs: '',
    });
    if (sourceInfo.event) {
      setFormValues({
        ...formValues,
        [sourceInfo.event.target.id]: values.value === '' ? '' : +values.value,
      });
    }
  };

  const handleSeqNoChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.event) {
      setFormValues({ ...formValues, [sourceInfo.event.target.id]: values.value });
      setFormErrors({ ...formErrors, [sourceInfo.event.target.id]: '' });
    }
  };

  const handleWebDisplayChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.event) {
      setFormValues({ ...formValues, [sourceInfo.event.target.id]: values.value });
      setFormErrors({ ...formErrors, [sourceInfo.event.target.id]: '' });
    }
  };

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleBohDescChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormErrors({
      ...formErrors,
      displayDesc: '',
    });
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleServiceGrntdChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, serviceGuaranteed: event.target.id === 'serviceGuaranteedYes' });
  };

  const handleValidOnWebChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, validOnWeb: event.target.id === 'validOnWebYes' });
  };

  const handleOverrideKitchenPrintChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, overrideKitchenPrint: event.target.id === 'overrideKitchenYes' });
  };

  const handleTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setFormErrors({ ...formErrors, [event.target.id]: '' });
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleSave = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    let error = false;
    let newFormErrors = {
      displayDesc: '',
      featureRefs: '',
      seqNo: '', 
      synopsis: '',
      webDisplay: '',
    };

    if (formValues.displayDesc?.length > 9) {
      error = true;
      newFormErrors.displayDesc = 'BOH Description must be 9 characters or less';
    }

    if (!formValues.displayDesc || formValues.displayDesc.length < 1) {
      error = true;
      newFormErrors.displayDesc = 'Please enter a BOH Description';
    }

    if (+formValues.featureRefs > 2147483646) {
      error = true;
      newFormErrors.featureRefs = 'Feature Refs is too long';
    }

    if ([null, undefined, -1, ''].includes(formValues.seqNo)) {
      error = true;
      newFormErrors.seqNo = 'Sequence Number is required.';
    }

    if(formValues.seqNo?.length > 3) {
      error = true;
      newFormErrors.seqNo = 'Sequence Number must be 3 characters or less';
    }

    if (formValues.synopsis?.length > 255) {
      error = true;
      newFormErrors.synopsis = 'Synopsis must be 255 characters or less';
    }

    if ([null, undefined, -1, ''].includes(formValues.webDisplay)) {
      error = true;
      newFormErrors.webDisplay = 'Web Display Sequence Number is required.';
    }

    if (+formValues.webDisplay > 2147483646) {
      error = true;
      newFormErrors.webDisplay = 'Web Display Sequence Number is too long';
    }

    if (!error) {
      setUnsavedChanges(false);
      mutate(
        {
          classNo: +_classNo,
          allowToppingTypeNo: +formValues.allowToppingTypeNo,
          serviceGuaranteed: formValues.serviceGuaranteed,
          validOnWeb: formValues.validOnWeb,
          overrideKitchenPrint: formValues.overrideKitchenPrint,
          featureRefs: +formValues.featureRefs,
          displayDesc: formValues.displayDesc,
          statusNo: +formValues?.statusNo || 0,
          seqNo: +formValues.seqNo,
          synopsis: formValues.synopsis,
          webDisplay: +formValues.webDisplay,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['products', _productNo, _classNo]);
            navigateWithToast('/tables/products', {
              label: 'Product has been updated successfully.',
              id: 'productUpdateSuccess',
            });
          },
        }
      );
    } else {
      setFormErrors(newFormErrors);
    }
  };

  return (
    <>
      <Header
        breadcrumb={{ label: 'Back to Table', to: '/tables/products' }}
        title={`View Product ${productData?.productNo ? `- ${productData?.productNo}` : ''}`}
      />
      <Form>
        <FormElements>
          <H2>Information</H2>
          <RowWrapper>
            <InputWrapperMedium>
              <Input label='MOA Class Number' id='moaClassNo' value={productData?.classNo?.toString()} disabled />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input label='MOA Product Number' id='moaProductNo' value={productData?.productNo?.toString()} disabled />
            </InputWrapperMedium>
          </RowWrapper>
          <RowWrapper>
            <InputWrapperMedium>
              <Input label='Product Number' id='productNo' value={productData?.productNo?.toString()} disabled />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <MaskedInput
                label='Sequence Number'
                id='seqNo'
                value={formValues?.seqNo.toString()}
                errorText={formErrors?.seqNo}
                isAllowed={(values) => +values.value < 2147483648}
                onValueChange={handleSeqNoChange}
              />
            </InputWrapperMedium>
          </RowWrapper>
          <InputWrapperLarge>
            <Input
              label='BOH Description'
              id='displayDesc'
              value={formValues.displayDesc}
              onChange={handleBohDescChange}
              errorText={formErrors.displayDesc}
            />
          </InputWrapperLarge>
          <RowWrapper>
            <InputWrapperLarge>
              <Input label='Short Description' id='description' value={productData?.description} disabled />
            </InputWrapperLarge>
            <InputWrapperLarge>
              <Input label='Long Description' id='longDescription' value={productData?.longDescription} disabled />
            </InputWrapperLarge>
          </RowWrapper>
          <TextAreaWrapper>
            <Textarea
              label='Synopsis'
              id='synopsis'
              value={productData?.synopsis}
              onChange={handleTextAreaChange}
              errorText={formErrors.synopsis}
            />
          </TextAreaWrapper>
          <RowWrapper>
            <DropdownWrapperLarge>
              <Dropdown
                label='Status'
                id='statusNo'
                options={Object.keys(STATUS).map((key: any) => {
                  return { value: key, label: `${key} - ${STATUS[key as StatusKeys]}` };
                })}
                value={productData?.statusNo?.toString()}
                onChange={handleSelectChange}
              />
            </DropdownWrapperLarge>
            <DropdownWrapperLarge>
              <Dropdown
                label='Allow Topping Type Number'
                id='allowToppingTypeNo'
                options={Object.keys(ALLOW_TOPPING_TYPE).map((key: any) => {
                  return { value: key, label: `${ALLOW_TOPPING_TYPE[key as AllowTopTypeKeys]}` };
                })}
                value={formValues.allowToppingTypeNo}
                onChange={handleSelectChange}
              />
            </DropdownWrapperLarge>
          </RowWrapper>
          <RowWrapper>
            <InputWrapperMedium>
              <Input label='Max Toppings' id='maxToppings' value={productData?.maxTopping?.toString()} disabled />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input
                label='Max Free Toppings'
                id='maxFreeToppings'
                value={productData?.maxFreeTopping?.toString()}
                disabled
              />
            </InputWrapperMedium>
          </RowWrapper>
          <RowWrapper>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'serviceGuaranteedYes',
                    label: 'Yes',
                  },
                  {
                    id: 'serviceGuaranteedNo',
                    label: 'No',
                  },
                ]}
                id='serviceGuaranteed'
                label='Service Guaranteed'
                layout={RadioGroupLayouts.Row}
                selected={formValues?.serviceGuaranteed ? 'serviceGuaranteedYes' : 'serviceGuaranteedNo'}
                onChange={handleServiceGrntdChange}
              />
            </RadioWrapper>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'overrideKitchenYes',
                    label: 'Yes',
                  },
                  {
                    id: 'overrideKitchenNo',
                    label: 'No',
                  },
                ]}
                id='overrideKitchenPrint'
                label='Override Kitchen Print'
                layout={RadioGroupLayouts.Row}
                selected={productData?.overrideKitchenPrint ? 'overrideKitchenYes' : 'overrideKitchenNo'}
                onChange={handleOverrideKitchenPrintChange}
              />
            </RadioWrapper>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'validOnWebYes',
                    label: 'Yes',
                  },
                  {
                    id: 'validOnWebNo',
                    label: 'No',
                  },
                ]}
                id='validOnWeb'
                label='Valid on Web?'
                layout={RadioGroupLayouts.Row}
                selected={productData?.validOnWeb ? 'validOnWebYes' : 'validOnWebNo'}
                onChange={handleValidOnWebChange}
              />
            </RadioWrapper>
          </RowWrapper>
          <RowWrapper>
            <InputWrapperMedium>
              <MaskedInput
                label='Feature Refs'
                id='featureRefs'
                value={formValues.featureRefs}
                onValueChange={handleFeatureRefsChange}
                errorText={formErrors.featureRefs}
                format='##########'
                testId={'featureRefsMaskedInput'}
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input label='Mnemonic' id='mnemonic' value={productData?.mnemonic?.toString()} disabled />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <MaskedInput
                label='Web Display Sequence No.'
                id='webDisplay'
                value={productData?.webDisplay?.toString()}
                onValueChange={handleWebDisplayChange}
                errorText={formErrors.webDisplay}
                decimalScale={0}
                allowNegative={false}
                testId={'webDisplayMaskedInput'}
              />
            </InputWrapperMedium>
          </RowWrapper>
          { classNo &&
              productNo &&
              <>
                <HorizontalLine />
                <ImageForm resourceNo={[Number(classNo), Number(productNo)]} attribute={'prod'} />
              </>
          }
        </FormElements>
        <Buttons>
          <Button label='Save' onClick={handleSave} disabled={!unsavedChanges} isLoading={isLoading} />
        </Buttons>
      </Form>
      <SubTitle>{readOnlyLabelTableText}</SubTitle>
      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
    </>
  );
};

export default EditProduct;
